import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useState} from 'react';
import _ from 'underscore';

import {makeAnalyticsEventId} from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import {ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT} from 'auth/DefaultSchemaCollaboratorsConfiguration';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {SavingState} from 'constants/RegistryConstants';
import * as Context from 'Context';
import {
  ActionMethod,
  ActionOrigin,
  LegacyAssaySchemaType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import FieldTable, {ClientSchemaField, createClientSchemaField} from 'registry/field/FieldTable';
import {AssaySchema, SCHEMA_TYPE_NAME} from 'results/assay-schemas/AssaySchemaConstants';
import * as AssaySchemaDAO from 'results/assay-schemas/AssaySchemaDAO';
import {ResultSchemaFieldTableRow} from 'results/assay-schemas/AssaySchemaFieldTableRow';
import {assaySchemaEditorStyles} from 'results/assay-schemas/AssaySchemaMetadataSettings';
import SchemaMetadataSection from 'results/assay-schemas/SchemaMetadataSection';
import {dispatchAssaySchemaFetched} from 'results/AssaySchemaActions';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import CreateSchemaModal from 'schemas/CreateSchemaModal';
import {getOwnerById, massageFieldsForSubmit} from 'schemas/SchemaUtil';
import {SchemaWithOrgPermissionsSettings as CreateSchemaWithOrgPermissionsSettings} from 'schemas/SchemaWithOrgPermissionsSettings';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import {InlineBannerType, InlineBannerProps} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {FieldNamingException} from 'warehouse/nameToSqlIdentifier';

import {LinkableSchemasQuery} from './__generated/LinkableSchemasQuery';
import {ResultSchemaModalQuery, ResultSchemaModalQueryVariables} from './__generated/ResultSchemaModalQuery';

type Organization = ExtractByTypename<ResultSchemaModalQuery['organization'], 'OrganizationNode'>;

export interface BaseCreateResultSchemaModalProps {
  newFieldNames: Array<string>;
  ownerId: string;
  onRequestClose: () => void;
  onRequestSubmit: (resultSchema: AssaySchema) => void;
}

interface QueryProps {
  organization: Organization;
  assaySchemas: Array<AssaySchema>;
  storableSchemas: Array<StorableSchema>;
}

type CreateResultSchemaModalProps = BaseCreateResultSchemaModalProps & QueryProps;

function CreateResultSchemaModal(props: CreateResultSchemaModalProps): JSX.Element {
  const ownerChoices = getSchemaOwnerChoices().filter((o) => o.id === props.ownerId);
  const [name, setName] = useState('');
  const initialFields: Array<ClientSchemaField> = props.newFieldNames.map((fieldName) =>
    createClientSchemaField(fieldName)
  );
  const [fields, setFields] = useState(initialFields);
  const [errorBannerProps, setErrorBannerProps] = useState(null as InlineBannerProps | null);

  function performValidation(): boolean {
    if (name.trim().length === 0) {
      setErrorBannerProps({bannerType: InlineBannerType.ERROR, text: 'Schema needs a name'});
      return false;
    }
    for (const field of fields) {
      if (field.name.length === 0) {
        setErrorBannerProps({bannerType: InlineBannerType.ERROR, text: 'All fields must have a name'});
        return false;
      }
      if (_.where(fields, {name: field.name}).length > 1) {
        setErrorBannerProps({
          bannerType: InlineBannerType.ERROR,
          text: 'You may not add two fields with the same name',
        });
        return false;
      }
    }
    setErrorBannerProps(null);
    return true;
  }

  function createSchema(collaborators: Array<SchemaCollaborator>): void {
    let massagedFields;
    try {
      massagedFields = massageFieldsForSubmit(fields);
    } catch (e) {
      if (e instanceof FieldNamingException) {
        setErrorBannerProps({bannerType: InlineBannerType.ERROR, text: e.message});
        return;
      } else {
        throw e;
      }
    }
    const spec = {fields: massagedFields};
    if (spec) {
      Context.using(
        {
          analyticsMetadata: {
            actionOrigin: ActionOrigin.SETTINGS_ASSAY_SCHEMA_PAGE_UI,
            actionMethod: ActionMethod.CREATE_MODAL,
          },
        },
        null,
        () => {
          AssaySchemaDAO.createAssaySchema({
            ownerId: props.ownerId,
            name,
            spec,
            schemaType: LegacyAssaySchemaType.ASSAY_RESULT,
            sqlIdentifier: null,
            isSpecJsonFormat: false,
            publishDefaultDataTableConfig: true,
            collaborators,
            sessionId: makeAnalyticsEventId(),
          })
            .done((response) => {
              const resultSchema = response!.assaySchema;
              dispatchAssaySchemaFetched(resultSchema);
              props.onRequestSubmit(resultSchema);
            })
            .fail((response) => {
              notifications.notifyError(response);
            });
        }
      );
    }
  }

  function renderMetadataSection(): JSX.Element {
    return (
      <div className="u-flex-grow u-min-width-zero">
        <div className="settingsPageContainer-section">
          <SchemaMetadataSection
            isEditableWarehouseTableName={false}
            name={name}
            ownerId={props.ownerId}
            ownerChoices={ownerChoices}
            sqlIdentifier=""
            onNameChange={(newName: string) => {
              setName(newName);
              setErrorBannerProps(null);
            }}
            onOwnerChange={noop}
            onSqlIdentifierChange={noop}
            schema={null}
          />
        </div>
        <div className={css(assaySchemaEditorStyles.fieldSection)}>
          <FieldTable // TODO (BNCH-53233): Render FieldTableLoading while fetching data
            schemaId={null}
            schema={null}
            schemaHasData={false}
            schemaFieldDependentsSummariesById={{}}
            isBeingCreated={true}
            isDisabled={false}
            itemClass={ResultSchemaFieldTableRow}
            fields={fields}
            onFieldsChange={(newFields) => {
              setFields(newFields);
              setErrorBannerProps(null);
            }}
            schemaType={SCHEMA_TYPE_NAME[LegacyAssaySchemaType.ASSAY_RESULT]}
            batchSchemas={[]}
            storableSchemas={props.storableSchemas}
            assaySchemas={props.assaySchemas}
            savingState={SavingState.COMPLETED}
            ownerUrlId={getOwnerById(props.ownerId)?.url_identifier ?? ''}
            ownerId={props.ownerId}
            shouldShowIsRequired={true}
            shouldShowIsIntegration={false}
            shouldShowIsParentLink={false}
            dataTestAttrs={{component: 'CreateResultSchemaModal', element: 'fieldTable'}}
          />
        </div>
      </div>
    );
  }

  return (
    <CreateSchemaModal
      title="Create result schema"
      newFieldNames={props.newFieldNames}
      onRequestClose={props.onRequestClose}
      organization={props.organization}
      createSchema={createSchema}
      isSchemaPermissionsEnabled={BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS}
      renderMetadataSection={renderMetadataSection}
      permissionsSettingsClass={CreateSchemaWithOrgPermissionsSettings}
      bannerProps={errorBannerProps}
      performValidation={performValidation}
    />
  );
}

export const RESULT_SCHEMA_MODAL_QUERY: DocumentNode = gql`
  query ResultSchemaModalQuery($orgId: StrictString!) @skipAudit @engComponent(name: RESULTS) {
    organization(id: $orgId) {
      ... on OrganizationNode {
        ...OrganizationDefaultSchemaCollaboratorsConfiguration
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${ORGANIZATION_DEFAULT_SCHEMA_COLLABORATORS_CONFIGURATION_FRAGMENT}
`;

export const LINKABLE_SCHEMAS_QUERY = gql`
  query LinkableSchemasQuery @skipAudit @engComponent(name: RESULTS) {
    ...AllAssaySchemasGraphQL
    ...AllStorableSchemasGraphQL
  }
  ${getFragmentWrapper('AllAssaySchemas').fragment}
  ${getFragmentWrapper('AllStorableSchemas').fragment}
`;

export default function QueryingCreateResultSchemaModal(
  props: BaseCreateResultSchemaModalProps
): JSX.Element | null {
  const queryResult = useBenchlingQuery<ResultSchemaModalQuery, ResultSchemaModalQueryVariables>(
    RESULT_SCHEMA_MODAL_QUERY,
    {variables: {orgId: props.ownerId}}
  );
  const schemasResult = useBenchlingQuery<LinkableSchemasQuery>(LINKABLE_SCHEMAS_QUERY);
  const assaySchemas =
    schemasResult.type === QueryResultType.SUCCESS
      ? graphQLArrayToREST('AssaySchema', [
          ...schemasResult.data.requestSchemas,
          ...schemasResult.data.resultSchemas,
          ...schemasResult.data.runSchemas,
        ])
      : [];
  const storableSchemas =
    schemasResult.type === QueryResultType.SUCCESS
      ? graphQLArrayToREST('StorableSchema', [
          ...schemasResult.data.boxSchemas,
          ...schemasResult.data.containerSchemas,
          ...schemasResult.data.locationSchemas,
          ...schemasResult.data.plateSchemas,
        ])
      : [];

  if (queryResult.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  }
  const organization = queryResult.data.organization;
  if (isInaccessible(organization)) {
    assertAndContinue(false, 'Inaccessible organization');
    return null;
  }
  return (
    <CreateResultSchemaModal
      {...props}
      organization={organization}
      assaySchemas={assaySchemas}
      storableSchemas={storableSchemas}
    />
  );
}
